import { EffectComposer } from '@react-three/postprocessing'
import { useGeckoAuth } from 'features/auth'
import {
  PointCloudCache,
  usePointCloudContext,
  usePointCloudParams,
  ViewerMode,
} from 'features/pointClouds'
import { useRolodexEntries } from 'features/rolodex'
import { isNil } from 'lodash'
import { ObservationMarkers } from 'pages/pointClouds/components/observations/markers/ObservationMarkers'
import { FlightHandler } from 'pages/pointClouds/controls/FlightHandler'
import React, { useEffect, useRef, useState } from 'react'
import { Vector3 } from 'three'
import { GeckoSiteObservation, RolodexEntryType } from 'types'

import { EDLEffect } from './components/effects/EyeDomeLightingEffect'
import { WhitePointsEffect } from './components/effects/WhitePointsEffect'
import { PointCloudCanvas } from './components/PointCloudCanvas'
import { PointCloudTooltip } from './components/tooltips/PointCloudTooltip'
import { HoveredPointData } from './components/tooltips/usePointHover'
import { ViewerModeControls } from './components/ViewerModeControls'
import { handleViewerModeChange } from './controls/controls'
import { FloorPlane } from './FloorPlane'
import { MaxarTiles } from './Maxar3DTiles'
import { PointCloudLoader } from './PointCloudLoader'

interface PointCloudRendererProps {
  potreeMetadataFile?: string
}

export const PointCloudRenderer: React.FC<PointCloudRendererProps> = ({
  potreeMetadataFile = 'metadata.json',
}) => {
  const { authorizedAxios: axios } = useGeckoAuth()

  const {
    viewerMode,
    setViewerMode,
    edlStrength,
    showTiles,
    edlRadius,
    setHitPosition,
    observationPosition,
    setObservationPosition,
    setEditingObservation,
    activePointCloud,
    enableEdl,
    enableWhitePoints,
    potreePointClouds,
    flyToCoordinates,
    coordinatesToLookAtAfterFlight,
    enableTooltips,
  } = usePointCloudContext()

  const [tooltipData, setTooltipData] = useState<HoveredPointData | null>(null)
  const params = usePointCloudParams()
  const siteID = params?.siteID

  const { siteModels } = useRolodexEntries({
    type: [RolodexEntryType.SITE_MODEL],
    tags: { site: [siteID!] },
  })

  const siteModel = siteModels.at(0)

  const cacheRef = useRef(new PointCloudCache())

  useEffect(() => {
    if (observationPosition) {
      setViewerMode(ViewerMode.ENV_CONTROLS)
    }
  }, [observationPosition, setViewerMode])

  const handleLaserHit = (position: Vector3 | null) => {
    setHitPosition(position)
  }

  const handlePointClick = (position: Vector3) => {
    if (viewerMode !== ViewerMode.SELECTION) return
    setObservationPosition(position)
    setEditingObservation({} as GeckoSiteObservation)
  }

  const handlePointHover = (hoverData: HoveredPointData | null) => {
    setTooltipData(hoverData)
  }

  useEffect(() => {
    handleViewerModeChange(viewerMode, setViewerMode)
  }, [viewerMode, setViewerMode])
  return (
    <>
      <PointCloudCanvas>
        {showTiles && !isNil(siteModel?.id) ? (
          <MaxarTiles
            siteModelID={siteModel.id}
            siteModelOrientation={activePointCloud?.data.site_model_orientation}
          />
        ) : (
          <FloorPlane />
        )}
        <EffectComposer>
          {enableWhitePoints ? <WhitePointsEffect /> : <></>}
          {enableEdl ? (
            <EDLEffect radius={edlRadius} strength={edlStrength} />
          ) : (
            <></>
          )}
        </EffectComposer>
        <FlightHandler
          coordinateToFlyTo={flyToCoordinates}
          coordinatesToLookAtAfterFlight={coordinatesToLookAtAfterFlight}
        />
        <ViewerModeControls
          viewerMode={viewerMode}
          onHit={handleLaserHit}
          onClick={handlePointClick}
          onHover={handlePointHover}
          enableTooltips={enableTooltips}
        />
        <ObservationMarkers />
        <PointCloudLoader
          fileName={potreeMetadataFile}
          potreePointClouds={potreePointClouds}
          entryId={siteID!}
          axios={axios}
          cache={cacheRef.current}
        />
      </PointCloudCanvas>
      <PointCloudTooltip
        position={tooltipData?.screenPosition}
        pointMetadata={tooltipData?.pointMetadata}
      />
    </>
  )
}
